import { UserAvatarStack } from "@cmp-src/components/UserAvatar/UserAvatarStack";
import { IResource, ISchedule } from "@gettactic/api";
import {
  cn,
  formatStartEndSlotHours,
  useDisclosure
} from "@gettactic/helpers/src";
import {
  gerCroppedImageUrl,
  getResourceCoverUrl
} from "@gettactic/helpers/src/resources/getResourceCoverUrl";
import { truncateOfficeAndFloor } from "@gettactic/helpers/src/strings/truncateOfficeAndFloor";
import { useSaveResourceFavorite } from "@gettactic/hooks";
import { Heading } from "@v2/components/display/Heading/Heading";
import { Image } from "@v2/components/display/Image/Image";
import { Popover } from "@v2/components/overlay/Popover/Popover";
import { Badge } from "@v2/components/ui/badge";
import { Button } from "@v2/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@v2/components/ui/tooltip";
import { useIsMobile } from "@v2/hooks/use-mobile";
import { FavoriteFilledIcon, FavoriteIcon } from "@v2/icons";
import { useTactic } from "@v2/providers/TacticProvider/context";
import React from "react";
import { CapacityLabel } from "../CapacityLabel/CapacityLabel";

export interface ResourceLabelProps {
  resource: Partial<IResource>;
  className?: string;
  allowFavorite?: boolean;
  onMouseEnter?: (event: React.MouseEvent<HTMLDivElement>) => void;
  onMouseLeave?: (event: React.MouseEvent<HTMLDivElement>) => void;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  showPicture?: boolean;
  headingClassName?: string;
  infoWrapperClassName?: string;
  maxTags?: number;
  schedules?: ISchedule[];
  showHierarchy?: boolean;
  showAmenities?: boolean;
  headerRight?: React.ReactNode | undefined;
  hierarchyRight?: React.ReactNode | undefined;
  showAttenders?: boolean;
}

export const classes = {
  Wrapper: "group flex items-start gap-4",
  InfoSide: "flex flex-1 items-center justify-between min-w-0",
  ImageWrapper:
    "rounded-[5px] overflow-hidden bg-neutral-50 h-[73px] w-[73px] md:h-[74px] md:w-[74px] flex-shrink-0",
  Image: "h-[73px] md:h-[74px] object-cover",
  InfoWrapper: "flex w-full flex-1 flex-col gap-1.5 items-start min-w-0",
  ResourceLocation:
    "text-[13px] text-muted-foreground flex items-center gap-1 capitalize font-inter min-w-0 overflow-hidden",
  ResourceName: "truncate",
  ResourceTagsWrapper:
    "text-[12px] text-muted-foreground flex gap-1 justify-start",
  FavoriteWrapper:
    "self-start opacity-0 transition-opacity group-hover:opacity-100 shrink-0",
  FavoriteWrapperMobile: "self-start opacity-1 transition-opacity shrink-0"
};

export const ResourceLabel: React.FC<ResourceLabelProps> = ({
  resource,
  className,
  allowFavorite = true,
  onMouseLeave,
  onMouseEnter,
  onClick,
  showPicture = true,
  headingClassName,
  infoWrapperClassName,
  maxTags = 2,
  schedules,
  showHierarchy = true,
  showAmenities = true,
  headerRight,
  hierarchyRight,
  showAttenders = false
}) => {
  const { divisionCategories } = useTactic();
  const { toggleResourceFavorite, isLoading: isFavoriteLoading } =
    useSaveResourceFavorite();

  const amenities = React.useMemo(() => {
    if (!showAmenities || !resource?.tags || resource.tags.length === 0)
      return [];
    return resource.tags.filter((tag) => tag.type === "amenity");
  }, [resource?.tags, maxTags, showAmenities]);

  const tagsToShow = React.useMemo(() => {
    if (!amenities || amenities.length === 0) return [];
    return amenities.slice(0, maxTags);
  }, [amenities]);

  if (!resource) return null;

  const office = resource.office_id
    ? divisionCategories.offices[resource.office_id]
    : undefined;
  const floor = office?.floors.find((floor) => floor.id === resource.parent_id);
  const coverImageUrl = getResourceCoverUrl(resource, 300);
  const tagsPopoverDisclosure = useDisclosure();
  const isMobile = useIsMobile();
  const maxLength = hierarchyRight ? 36 : 40;
  const truncatedNames = React.useMemo(() => {
    return truncateOfficeAndFloor(
      office?.name || "",
      floor?.name || "",
      undefined,
      isMobile ? maxLength * 1.5 : maxLength
    );
  }, [office?.name, floor?.name, isMobile, maxLength]);

  return (
    <div
      className={cn(classes.Wrapper, className)}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {showPicture && (
        <div className={classes.ImageWrapper}>
          <Image
            src={
              coverImageUrl ||
              gerCroppedImageUrl(
                "https://cdn.gettactic.com/defaults/room.png",
                300
              )
            }
            className={classes.Image}
          />
        </div>
      )}
      <div className={classes.InfoSide}>
        <div className={cn(classes.InfoWrapper, infoWrapperClassName)}>
          <div className="flex w-full items-center justify-between">
            <Heading
              as={"h5"}
              className={cn(classes.ResourceName, headingClassName)}
            >
              {resource.name}
            </Heading>
            {headerRight}
          </div>
          {showHierarchy && (
            <div className="flex w-full min-w-0 items-center justify-between gap-1 truncate md:max-w-auto">
              <div className={classes.ResourceLocation}>
                <span className="hidden md:inline">
                  {truncatedNames.officeName}
                </span>
                <span className="hidden shrink-0 md:inline">·</span>
                <span>{truncatedNames.floorName}</span>
                <CapacityLabel resource={resource} />
              </div>

              {!!hierarchyRight && (
                <div className="shrink-0 text-[13px] text-muted-foreground">
                  {hierarchyRight}
                </div>
              )}
            </div>
          )}
          {showAmenities && (
            <p className={classes.ResourceTagsWrapper}>
              <TooltipProvider>
                {(tagsToShow.length || 0) > 0
                  ? tagsToShow.map((tag) => (
                      <Tooltip key={tag.id}>
                        <TooltipTrigger asChild>
                          <Badge
                            variant={"secondary"}
                            className={
                              "inline-block min-w-0 max-w-[100px] truncate bg-[#EBEEF2] font-inter font-normal text-[#7E7E7E] capitalize"
                            }
                            size={"xsm"}
                          >
                            {tag.name}
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent>
                          <span className={"text-[13px]"}>{tag.name}</span>
                        </TooltipContent>
                      </Tooltip>
                    ))
                  : null}
              </TooltipProvider>
              {tagsToShow.length < (resource.tags?.length || 0) ? (
                <Popover
                  triggerAsChild
                  open={tagsPopoverDisclosure.isOpen}
                  triggerClassname={"w-fit"}
                  content={
                    <div
                      className={
                        "flex max-w-[300px] flex-wrap justify-center gap-2"
                      }
                    >
                      {(amenities.length || 0) > 0
                        ? amenities.map((tag) => (
                            <Badge
                              variant={"outline"}
                              key={tag.id}
                              size={"xsm"}
                            >
                              {tag.name}
                            </Badge>
                          ))
                        : null}
                    </div>
                  }
                >
                  <div>
                    <Badge
                      variant={"outline"}
                      key={"plus"}
                      onMouseEnter={tagsPopoverDisclosure.onOpen}
                      onMouseLeave={tagsPopoverDisclosure.onClose}
                      className={"inline-block"}
                      size={"xsm"}
                    >
                      +{(resource.tags?.length || 0) - tagsToShow.length}
                    </Badge>
                  </div>
                </Popover>
              ) : undefined}
            </p>
          )}
          {schedules && schedules.length > 0 && (
            <p
              className={cn(
                "text-[13px] text-black",
                "flex w-full items-center gap-2"
              )}
            >
              {formatStartEndSlotHours(schedules[0], " → ")}
              {schedules.length > 1 && (
                <span className={"text-muted-foreground"}>
                  + {schedules.length - 1} more
                </span>
              )}
              {showAttenders && (
                <span className={"flex flex-1 justify-end"}>
                  <UserAvatarStack
                    users={schedules[0].slots.map((slot) => slot.user)}
                  />
                </span>
              )}
            </p>
          )}
        </div>
        {allowFavorite && (
          <div
            className={cn(
              isMobile ? classes.FavoriteWrapperMobile : classes.FavoriteWrapper
            )}
          >
            <Button
              disabled={isFavoriteLoading}
              variant={"icon"}
              onClick={async (event) => {
                event.stopPropagation();
                event.preventDefault();

                if (resource) await toggleResourceFavorite(resource);
              }}
            >
              {resource.favorite_id ? (
                <FavoriteFilledIcon className={"size-3.5"} />
              ) : (
                <FavoriteIcon className={"size-3.5"} />
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
